import * as THREE from 'three';

// Stars (upper hemisphere, twinkling) + a moon opposite the sun.
// Adds variety to the otherwise monochromatic warm sky.

export function createCelestial(sunDir) {
  const group = new THREE.Group();

  // ---- Stars ----
  const COUNT = 320;
  const positions = new Float32Array(COUNT * 3);
  const phases = new Float32Array(COUNT);
  const sizes = new Float32Array(COUNT);
  for (let i = 0; i < COUNT; i++) {
    // weight toward upper sky
    const phi = Math.acos(0.25 + Math.random() * 0.75);
    const theta = Math.random() * Math.PI * 2;
    const r = 2100;
    positions[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.cos(phi);
    positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
    phases[i] = Math.random() * Math.PI * 2;
    sizes[i] = 1.5 + Math.random() * 2.5;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('phase', new THREE.BufferAttribute(phases, 1));
  geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));

  const starMat = new THREE.ShaderMaterial({
    uniforms: { uTime: { value: 0 }, uSunDir: { value: sunDir.clone() } },
    vertexShader: /* glsl */`
      attribute float phase;
      attribute float aSize;
      varying float vAlpha;
      uniform float uTime;
      uniform vec3 uSunDir;
      void main() {
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = aSize;
        gl_Position = projectionMatrix * mv;
        // Twinkle
        float twinkle = 0.35 + 0.65 * sin(uTime * 1.1 + phase);
        // Fade in the half of the sky away from the sun (brighter sun-side washes them out)
        vec3 dir = normalize(position);
        float sunFade = clamp(0.5 - dot(dir, uSunDir) * 0.8, 0.0, 1.0);
        vAlpha = twinkle * sunFade;
      }
    `,
    fragmentShader: /* glsl */`
      varying float vAlpha;
      void main() {
        vec2 c = gl_PointCoord - 0.5;
        float d = length(c);
        if (d > 0.5) discard;
        float a = smoothstep(0.5, 0.0, d) * vAlpha * 0.85;
        gl_FragColor = vec4(1.0, 0.95, 0.82, a);
      }
    `,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    fog: false
  });

  const stars = new THREE.Points(geo, starMat);
  stars.frustumCulled = false;
  stars.renderOrder = -0.5;
  group.add(stars);

  // ---- Moon ----
  const moonDir = sunDir.clone().negate();
  if (moonDir.y < 0.35) moonDir.y = 0.45;
  moonDir.normalize();
  const moonPos = moonDir.clone().multiplyScalar(1900);

  // Soft outer halo
  const halo = new THREE.Mesh(
    new THREE.SphereGeometry(48, 24, 16),
    new THREE.MeshBasicMaterial({ color: 0xfff0d0, transparent: true, opacity: 0.12, fog: false, depthWrite: false })
  );
  halo.position.copy(moonPos);
  halo.frustumCulled = false;
  halo.renderOrder = -0.4;
  group.add(halo);

  // Moon disk
  const moon = new THREE.Mesh(
    new THREE.SphereGeometry(28, 24, 16),
    new THREE.MeshBasicMaterial({ color: 0xf8eed8, fog: false })
  );
  moon.position.copy(moonPos);
  moon.frustumCulled = false;
  moon.renderOrder = -0.3;
  group.add(moon);

  function update(time) {
    starMat.uniforms.uTime.value = time;
  }

  return { group, update };
}
